import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Megaphone, Sparkles, Eye, CheckCircle2, X, Loader2, Image as ImageIcon,
  Instagram, Facebook, AlertTriangle, RefreshCw, BarChart3, Wand2, ChevronDown, Mail,
  Upload, Film, Camera, Calendar, Clock, Video, Link as LinkIcon, TrendingUp,
  Send, UserPlus, MessageCircle, Trash2, LayoutDashboard, Search, ExternalLink, Activity, Rocket, Brain,
} from "lucide-react";
import { WeeklyPlanView } from "./WeeklyPlanView";
import { BrandPositionToggle } from "./BrandPositionToggle";
import { BrainPanel } from "./BrainPanel";
import { EmptyState, DraftCard, ScheduledCard, PublishedCard, Stat } from "./doozyad/Cards";
import { InfluencersPanel } from "./doozyad/Influencers";
import { TrendsPanel } from "./doozyad/Trends";
import { RetargetPanel } from "./doozyad/Retargeting";
import { CommandCenter } from "./doozyad/CommandCenter";
import { DiscoverHashtagPanel } from "./doozyad/DiscoverHashtagPanel";
import { DashboardCards } from "./doozyad/Dashboard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DoozyAdTab — Phase 2.
 *
 * Sections:
 *   1. Config banner
 *   2. AI image panel (Gemini Nano Banana)
 *   3. Sora 2 Reels panel (NEW)
 *   4. Single upload + Bulk upload panels
 *   5. Tabs: Pending · Scheduled · Published · 7-day Dashboard
 */
export default function DoozyAdTab({ auth, authAxios }) {
  const [config, setConfig] = useState(null);
  const [themes, setThemes] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [published, setPublished] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("party_in_2_hours");
  const [toneOverride, setToneOverride] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview"); // overview | queue | scheduled | published | trends | retarget | influencers | dashboard
  const [digestPreview, setDigestPreview] = useState(null);
  const [showDigestModal, setShowDigestModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ─── Command Center (Phase 3 capstone) ───
  const [commandCenter, setCommandCenter] = useState(null);
  const [roas, setRoas] = useState(null);
  const [discoverHashtag, setDiscoverHashtag] = useState("");
  const [discoverResult, setDiscoverResult] = useState(null);
  const [discoverRunning, setDiscoverRunning] = useState(false);

  const loadCommandCenter = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/command-center`);
      setCommandCenter(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load Command Center");
    }
  }, [authAxios]);

  const loadRoas = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/roas?days=7`);
      setRoas(r.data);
    } catch (e) {
      setRoas({ status: "error", error: e?.response?.data?.detail || "ROAS fetch failed" });
    }
  }, [authAxios]);

  const runDiscover = async () => {
    if (!discoverHashtag.trim()) return;
    setDiscoverRunning(true); setDiscoverResult(null);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/influencers/discover`, {
        hashtag: discoverHashtag.trim().replace(/^#/, ""),
        min_followers: 5000, max_followers: 50000, max_results: 15,
      }, { timeout: 120000 });
      setDiscoverResult(r.data);
      if (r.data.status === "ok" && r.data.added > 0) {
        await loadInfluencers();
      }
    } catch (e) {
      setDiscoverResult({ status: "error", error: e?.response?.data?.detail || "Discovery failed" });
    }
    setDiscoverRunning(false);
  };

  // ─── Sora 2 video generation ───
  const [videoTheme, setVideoTheme] = useState("party_in_2_hours");
  const [videoCustomPrompt, setVideoCustomPrompt] = useState("");
  const [videoModel, setVideoModel] = useState("sora-2");
  const [videoSize, setVideoSize] = useState("1024x1792");
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoJobStatus, setVideoJobStatus] = useState("");
  // Fire-and-forget job tracking — each entry: {job_id, label, started_at}
  const [activeVideoJobs, setActiveVideoJobs] = useState([]);
  // Ref mirror so the poller useEffect doesn't need to re-subscribe on every state change.
  const activeJobsRef = useRef(activeVideoJobs);
  useEffect(() => { activeJobsRef.current = activeVideoJobs; }, [activeVideoJobs]);

  // ─── Phase 3: Trend Mining ───
  const [festivals, setFestivals] = useState([]);
  const [googleTrends, setGoogleTrends] = useState(null);
  const [festivalRunning, setFestivalRunning] = useState(false);
  const [trendBrainstorming, setTrendBrainstorming] = useState(false);

  // ─── Phase 3 Sprint 2: Retargeting ───
  const [retargetQueue, setRetargetQueue] = useState([]);
  const [retargetScanning, setRetargetScanning] = useState(false);
  const [retargetAudienceFilter, setRetargetAudienceFilter] = useState("");

  const loadRetargetQueue = useCallback(async () => {
    const url = retargetAudienceFilter
      ? `${API_URL}/api/admin/doozyad/retargeting/queue?audience=${retargetAudienceFilter}`
      : `${API_URL}/api/admin/doozyad/retargeting/queue`;
    try {
      const r = await authAxios.get(url);
      setRetargetQueue(r.data?.rows || []);
    } catch { /* ignore */ }
  }, [authAxios, retargetAudienceFilter]);

  const runRetargetScan = async () => {
    if (!window.confirm("Scan all 3 audiences and queue messages? This calls Gemini for each candidate (can take 1-3 minutes for ~25 candidates).")) return;
    setRetargetScanning(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/retargeting/scan`, null, { timeout: 600000 });
      alert(`Retargeting scan: queued ${r.data.queued_total} messages\n${JSON.stringify(r.data.by_audience, null, 2)}`);
      await loadRetargetQueue();
    } catch (e) {
      setError(e?.response?.data?.detail || "Retargeting scan failed");
    }
    setRetargetScanning(false);
  };

  // ─── Phase 3 Sprint 3: Influencer Outreach ───
  const [influencers, setInfluencers] = useState([]);
  const [influencerStatusFilter, setInfluencerStatusFilter] = useState("");

  const loadInfluencers = useCallback(async () => {
    const url = influencerStatusFilter
      ? `${API_URL}/api/admin/doozyad/influencers?status=${influencerStatusFilter}`
      : `${API_URL}/api/admin/doozyad/influencers`;
    try {
      const r = await authAxios.get(url);
      setInfluencers(r.data?.influencers || []);
    } catch { /* ignore */ }
  }, [authAxios, influencerStatusFilter]);

  // Defined early (BEFORE useEffect poller on L217+) to avoid Temporal Dead Zone
  // errors — useEffect dep arrays are evaluated synchronously at render time,
  // so `refreshAll` must already be bound by the time we reach the poller effect.
  const refreshAll = useCallback(async () => {
    // Resilient loader: each endpoint loads independently. One failing endpoint
    // (e.g. a transient 500 on dashboard) MUST NOT blank-slate the entire tab.
    // Failures are logged per-endpoint so we can pinpoint prod issues fast.
    const endpoints = [
      ["config", `${API_URL}/api/admin/doozyad/config`, (d) => setConfig(d)],
      ["themes", `${API_URL}/api/admin/doozyad/themes`, (d) => setThemes(d?.themes || [])],
      ["drafts", `${API_URL}/api/admin/doozyad/drafts`, (d) => setDrafts(d?.drafts || [])],
      ["scheduled", `${API_URL}/api/admin/doozyad/scheduled`, (d) => setScheduled(d?.drafts || [])],
      ["published", `${API_URL}/api/admin/doozyad/published`, (d) => setPublished(d?.posts || [])],
      ["dashboard", `${API_URL}/api/admin/doozyad/dashboard`, (d) => setDashboard(d)],
      ["festivals", `${API_URL}/api/admin/doozyad/festivals`, (d) => setFestivals(d?.festivals || [])],
    ];
    const results = await Promise.allSettled(
      endpoints.map(([, url]) => authAxios.get(url, { timeout: 20000 }))
    );
    const failed = [];
    results.forEach((r, i) => {
      const [name, , setter] = endpoints[i];
      if (r.status === "fulfilled") {
        setter(r.value.data);
      } else {
        const code = r.reason?.response?.status;
        const detail = r.reason?.response?.data?.detail || r.reason?.message || "unknown";
        failed.push(`${name}${code ? ` (${code})` : ""}`);
        // eslint-disable-next-line no-console
        console.warn(`[DoozyAd] ${name} failed:`, code, detail);
      }
    });
    if (failed.length === endpoints.length) {
      setError("Could not reach DoozyAd backend. Check your connection and retry.");
    } else if (failed.length > 0) {
      setError(`Some panels couldn't load: ${failed.join(", ")}. Other tiles are usable.`);
    } else {
      setError("");
    }
  }, [authAxios]);

  const runFestivalPass = async () => {
    setFestivalRunning(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/cron/festival-now`, null, { timeout: 300000 });
      alert(`Festival pass: ${r.data.generated || 0}/${r.data.due || 0} drafts generated`);
      await refreshAll();
    } catch (e) {
      setError(e?.response?.data?.detail || "Festival pass failed");
    }
    setFestivalRunning(false);
  };

  const runTrendBrainstorm = async () => {
    setTrendBrainstorming(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/cron/trend-brainstorm-now`, null, { timeout: 300000 });
      const status = r.data.status;
      if (status === "generated") {
        alert(`Trend brainstorm done — ${r.data.generated_count} drafts created · top keyword: ${r.data.top_keyword || "(none)"}`);
        await refreshAll();
      } else {
        alert(`Trend brainstorm: ${status}`);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Trend brainstorm failed");
    }
    setTrendBrainstorming(false);
  };

  const generateVideo = async () => {
    if (!videoCustomPrompt.trim() && !videoTheme) {
      setError("Pick a theme or write a custom prompt for Sora 2");
      return;
    }
    setError(""); setVideoGenerating(true);
    const label = videoCustomPrompt.trim().slice(0, 40) || `theme · ${videoTheme}`;
    try {
      // Kick off async job (returns in <1s — no ingress timeout)
      const startRes = await authAxios.post(`${API_URL}/api/admin/doozyad/generate-video-draft`, {
        theme_id: videoTheme || undefined,
        custom_prompt: videoCustomPrompt || undefined,
        model: videoModel,
        size: videoSize,
        duration: videoDuration,
      }, { timeout: 30000 });

      const { job_id, estimated_seconds } = startRes.data || {};
      if (!job_id) throw new Error("No job_id returned from server");

      // Fire-and-forget: push to active jobs list. A shared useEffect poller
      // (below) watches each and toasts on done/failed. User can queue more.
      setActiveVideoJobs((prev) => [
        ...prev,
        { job_id, label, started_at: Date.now(), eta: estimated_seconds || 90 },
      ]);
      toast.info(`Sora 2 queued · ${label}`, {
        description: `ETA ~${estimated_seconds || 90}s · you can keep working or queue another.`,
        duration: 4000,
      });
      setVideoCustomPrompt("");
      setVideoJobStatus("");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Sora 2 generation failed — check backend logs";
      setError(msg);
      toast.error("Sora 2 queue failed", { description: msg });
    }
    setVideoGenerating(false);
  };

  // ─── Shared background poller for all fire-and-forget Sora 2 jobs ───
  useEffect(() => {
    if (activeVideoJobs.length === 0) return undefined;

    let cancelled = false;
    const POLL_MS = 6000;
    const MAX_ELAPSED_MS = 20 * 60 * 1000;

    const tick = async () => {
      const jobs = activeJobsRef.current;
      for (const job of jobs) {
        if (cancelled) return;
        const elapsedMs = Date.now() - job.started_at;
        if (elapsedMs > MAX_ELAPSED_MS) {
          setActiveVideoJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));
          toast.error(`Sora 2 timeout · ${job.label}`, {
            description: "No result after 20 minutes. Check backend logs.",
          });
          continue;
        }
        try {
          const r = await authAxios.get(
            `${API_URL}/api/admin/doozyad/video-job/${job.job_id}`,
            { timeout: 20000 }
          );
          const status = r.data?.status;
          if (status === "done") {
            setActiveVideoJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));
            toast.success(`Reel ready · ${job.label}`, {
              description: `Generated in ${r.data?.elapsed_s || Math.round(elapsedMs / 1000)}s. Added to the Pending queue.`,
              duration: 8000,
              action: { label: "Open queue", onClick: () => setActiveTab("queue") },
            });
            refreshAll();
          } else if (status === "failed") {
            setActiveVideoJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));
            toast.error(`Sora 2 failed · ${job.label}`, {
              description: (r.data?.error || "Unknown error").slice(0, 200),
              duration: 12000,
            });
          }
        } catch {
          // transient network hiccup — skip this tick; next tick will retry
        }
      }
    };

    tick(); // immediate poll on first job queued
    const iv = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [activeVideoJobs.length, authAxios, refreshAll]);

  const previewDigest = async () => {
    setPreviewLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/digest/preview`);
      setDigestPreview(r.data);
      setShowDigestModal(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load digest preview");
    }
    setPreviewLoading(false);
  };

  // ─── Upload your own media ───
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploadAutoCaptions, setUploadAutoCaptions] = useState(true);
  const [uploadTheme, setUploadTheme] = useState("party_in_2_hours");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadMedia = async () => {
    if (!uploadFile) return;
    setError(""); setUploading(true); setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("theme_id", uploadTheme);
      fd.append("provided_caption", uploadCaption);
      fd.append("auto_generate_captions", uploadAutoCaptions ? "true" : "false");
      await authAxios.post(`${API_URL}/api/admin/doozyad/upload-draft`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded * 100) / (p.total || 1))),
      });
      setUploadFile(null);
      setUploadCaption("");
      setUploadProgress(0);
      await refreshAll();
      setActiveTab("queue");
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  // ─── Bulk upload (Phase 2) ───
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkMode, setBulkMode] = useState("per_file_ai"); // "per_file_ai" | "shared"
  const [bulkSharedCaption, setBulkSharedCaption] = useState("");
  const [bulkTheme, setBulkTheme] = useState("party_in_2_hours");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const bulkMaxFiles = bulkMode === "per_file_ai" ? 10 : 20;

  const onBulkFilesPicked = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length > bulkMaxFiles) {
      setError(`Too many files: max ${bulkMaxFiles} for mode "${bulkMode}". You picked ${arr.length}.`);
      setBulkFiles(arr.slice(0, bulkMaxFiles));
    } else {
      setError("");
      setBulkFiles(arr);
    }
  };

  const uploadBulk = async () => {
    if (bulkFiles.length === 0) return;
    if (bulkMode === "shared" && !bulkSharedCaption.trim()) {
      setError("Shared caption required when 'one shared caption' mode is selected");
      return;
    }
    setError(""); setBulkUploading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      bulkFiles.forEach((f) => fd.append("files", f));
      fd.append("theme_id", bulkTheme);
      fd.append("caption_mode", bulkMode);
      fd.append("shared_caption", bulkSharedCaption);
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/upload-bulk`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setBulkResult(r.data);
      setBulkFiles([]);
      setBulkSharedCaption("");
      await refreshAll();
      setActiveTab("queue");
    } catch (e) {
      setError(e?.response?.data?.detail || "Bulk upload failed");
    }
    setBulkUploading(false);
  };

  // Lazy-load Command Center on tab open + ROAS in parallel
  useEffect(() => {
    if (activeTab === "overview" && !commandCenter) {
      loadCommandCenter();
      loadRoas();
    }
  }, [activeTab, commandCenter, loadCommandCenter, loadRoas]);

  // Lazy-load Google Trends only when Trends tab is opened (avoids slow boot)
  useEffect(() => {
    if (activeTab === "trends" && googleTrends === null) {
      authAxios
        .get(`${API_URL}/api/admin/doozyad/trends/google`, { timeout: 30000 })
        .then((r) => setGoogleTrends(r.data))
        .catch(() => setGoogleTrends({ status: "unavailable" }));
    }
  }, [activeTab, googleTrends, authAxios]);

  // Lazy-load retargeting queue when tab opens or filter changes
  useEffect(() => {
    if (activeTab === "retarget") loadRetargetQueue();
  }, [activeTab, loadRetargetQueue]);

  // Lazy-load influencers when tab opens or filter changes
  useEffect(() => {
    if (activeTab === "influencers") loadInfluencers();
  }, [activeTab, loadInfluencers]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const generateDraft = async () => {
    setError(""); setGenerating(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/generate-draft`, {
        theme_id: selectedTheme,
        tone_override: toneOverride || undefined,
      });
      await refreshAll();
      setActiveTab("queue");
    } catch (e) {
      setError(e?.response?.data?.detail || "Generation failed — check EMERGENT_LLM_KEY in backend env");
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-6" data-testid="doozyad-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit'] flex items-center gap-2">
            <Megaphone size={26} className="text-[#DC283C]" /> DoozyAd
          </h2>
          <p className="text-sm text-[#64748B] mt-1">
            AI-generated ad creative · approve → auto-publish to Instagram + Facebook · zero manual design work.
          </p>
        </div>
        <button
          onClick={refreshAll}
          className="px-3 py-2 text-xs bg-white border border-gray-200 rounded-xl hover:bg-gray-50 flex items-center gap-1.5"
          data-testid="doozyad-refresh-btn"
        >
          <RefreshCw size={13} /> Refresh
        </button>
        <button
          onClick={previewDigest}
          disabled={previewLoading}
          className="px-3 py-2 text-xs bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="doozyad-preview-digest-btn"
        >
          {previewLoading ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
          Preview Sunday digest
        </button>
      </div>

      {/* Meta config banner */}
      {config && !config.meta?.fully_configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="doozyad-config-banner">
          <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <b>Setup needed before posts go live:</b> drafts can be generated and reviewed now,
            but publishing to Instagram + Facebook is paused until you provide your Meta credentials.
            <ul className="mt-2 ml-4 text-xs list-disc space-y-0.5">
              <li>Instagram Business Account ID: {config.meta.ig_business_account_id_set ? "✅" : "❌ missing"}</li>
              <li>Facebook Page ID: {config.meta.fb_page_id_set ? "✅" : "❌ missing"}</li>
              <li>Meta Page Access Token: {config.meta.page_access_token_set ? "✅" : "❌ missing"}</li>
            </ul>
            <p className="mt-2 text-xs">
              Paste these in your next chat message — your agent will save them to backend env safely.
            </p>
          </div>
        </div>
      )}

      {/* Generate panel — hide on Overview to keep Command Center clean */}
      {activeTab !== "overview" && (<>
      <div className="bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
          <Wand2 size={16} className="text-[#DC283C]" /> Generate a new ad draft
        </h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Theme</label>
            <div className="relative">
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-pink-400 outline-none bg-white appearance-none pr-10"
                data-testid="doozyad-theme-select"
              >
                {themes.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.hook}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Tone (optional)</label>
            <input
              type="text"
              value={toneOverride}
              onChange={(e) => setToneOverride(e.target.value)}
              placeholder="e.g. fun + youthful"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-pink-400 outline-none"
              data-testid="doozyad-tone-input"
            />
          </div>
        </div>
        <button
          onClick={generateDraft}
          disabled={generating}
          className="mt-3 px-4 py-2 bg-gradient-to-r from-[#DC283C] to-purple-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
          data-testid="doozyad-generate-btn"
        >
          {generating ? <><Loader2 size={14} className="animate-spin" /> Generating image + 5 captions…</>
                      : <><Sparkles size={14} /> Generate draft</>}
        </button>
        {error && (
          <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="doozyad-error">
            {error}
          </div>
        )}
      </div>

      {/* Sora 2 Reels panel */}
      <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-1">
          <Video size={16} className="text-violet-600" /> Generate a Sora 2 Reel
        </h3>
        <p className="text-xs text-[#64748B] mb-4">
          AI video generation for vertical IG Reels. Pick a theme, optionally add a custom prompt, and choose
          length (4/8/12s) + aspect. Generation runs in the background (~1–3 min) — queue as many as you
          want, we'll toast you when each one lands.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Theme (auto-prompt)</label>
            <div className="relative">
              <select
                value={videoTheme}
                onChange={(e) => setVideoTheme(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-violet-400 outline-none bg-white appearance-none pr-10"
                data-testid="doozyad-video-theme-select"
              >
                <option value="">— No theme, prompt-only —</option>
                {themes.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.hook}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Model</label>
            <select
              value={videoModel}
              onChange={(e) => setVideoModel(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-violet-400 outline-none bg-white"
              data-testid="doozyad-video-model"
            >
              <option value="sora-2">sora-2 (faster, ~2-3 min)</option>
              <option value="sora-2-pro">sora-2-pro (higher quality, ~4-6 min)</option>
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Aspect / Size</label>
            <select
              value={videoSize}
              onChange={(e) => setVideoSize(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-violet-400 outline-none bg-white"
              data-testid="doozyad-video-size"
            >
              <option value="1024x1792">9:16 Vertical (Reels) · 1024×1792</option>
              <option value="1024x1024">1:1 Square (IG feed) · 1024×1024</option>
              <option value="1280x720">16:9 Landscape (HD) · 1280×720</option>
              <option value="1792x1024">Wide · 1792×1024</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Duration</label>
            <select
              value={videoDuration}
              onChange={(e) => setVideoDuration(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-violet-400 outline-none bg-white"
              data-testid="doozyad-video-duration"
            >
              <option value={4}>4 seconds (fastest)</option>
              <option value={8}>8 seconds</option>
              <option value={12}>12 seconds (longest)</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-semibold text-[#64748B] mb-1">Custom prompt (optional — overrides/augments theme)</label>
          <textarea
            value={videoCustomPrompt}
            onChange={(e) => setVideoCustomPrompt(e.target.value)}
            rows={2}
            placeholder="e.g. A young Indian woman walking into a balloon-arch surprise birthday setup at home, slow-motion, golden hour"
            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl focus:border-violet-400 outline-none"
            data-testid="doozyad-video-custom-prompt"
          />
        </div>
        <button
          onClick={generateVideo}
          disabled={videoGenerating || (!videoTheme && !videoCustomPrompt.trim())}
          className="mt-3 px-4 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
          data-testid="doozyad-video-generate-btn"
        >
          {videoGenerating
            ? <><Loader2 size={14} className="animate-spin" /> Queuing…</>
            : <><Video size={14} /> Queue Sora 2 Reel</>}
        </button>
        {activeVideoJobs.length > 0 && (
          <div
            className="mt-3 text-xs px-3 py-2 rounded-xl bg-violet-100 text-violet-900 border border-violet-200 flex items-center gap-2"
            data-testid="doozyad-active-video-jobs"
          >
            <Loader2 size={12} className="animate-spin shrink-0" />
            <span className="flex-1">
              <b>{activeVideoJobs.length}</b> Sora 2 job{activeVideoJobs.length === 1 ? "" : "s"} running in the background —
              you'll get a toast when {activeVideoJobs.length === 1 ? "it lands" : "each one lands"}.
              Latest: <span className="italic">{activeVideoJobs[activeVideoJobs.length - 1].label}</span>
            </span>
          </div>
        )}
        {videoJobStatus && (
          <div
            className="mt-3 text-xs px-3 py-2 rounded-xl bg-violet-100 text-violet-800 border border-violet-200 flex items-center gap-2"
            data-testid="doozyad-video-job-status"
          >
            <Loader2 size={12} className={videoGenerating ? "animate-spin" : ""} />
            <span>{videoJobStatus}</span>
          </div>
        )}
      </div>

      {/* Upload your own panel */}
      <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-1">
          <Upload size={16} className="text-emerald-600" /> Upload your own image or video
        </h3>
        <p className="text-xs text-[#64748B] mb-4">
          Got a designer-made image, a phone-shot reel, or UGC from a happy customer? Upload it here and
          DoozyAd will queue it for posting just like AI-generated drafts. Image: PNG/JPEG ≤ 8 MB ·
          Video: MP4/MOV ≤ 100 MB (≤ 60s for IG Reels).
        </p>

        <div className="grid sm:grid-cols-3 gap-3 items-start">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Choose file</label>
            <label
              htmlFor="doozyad-upload-input"
              className={`flex items-center gap-2 px-3 py-3 border-2 border-dashed rounded-xl cursor-pointer transition-colors text-xs ${uploadFile ? "border-emerald-400 bg-emerald-50/50" : "border-gray-300 hover:border-emerald-400 bg-white"}`}
              data-testid="doozyad-upload-dropzone"
            >
              {uploadFile ? (
                <>
                  {uploadFile.type.startsWith("video") ? <Film size={16} className="text-emerald-600" /> : <Camera size={16} className="text-emerald-600" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[#1E1B4B] font-semibold truncate">{uploadFile.name}</div>
                    <div className="text-[10px] text-[#64748B]">
                      {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB · {uploadFile.type}
                    </div>
                  </div>
                  <button type="button" onClick={(e) => { e.preventDefault(); setUploadFile(null); }}
                    className="p-1 hover:bg-white rounded">
                    <X size={13} />
                  </button>
                </>
              ) : (
                <>
                  <Upload size={16} className="text-gray-400" />
                  <span className="text-[#64748B]">Click or drop a PNG / JPEG / MP4 / MOV here</span>
                </>
              )}
            </label>
            <input
              id="doozyad-upload-input"
              type="file"
              accept="image/png,image/jpeg,video/mp4,video/quicktime"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="doozyad-upload-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Theme tag</label>
            <select
              value={uploadTheme}
              onChange={(e) => setUploadTheme(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl focus:border-emerald-400 outline-none bg-white"
              data-testid="doozyad-upload-theme"
            >
              {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs font-semibold text-[#64748B] mb-1">Caption (optional — leave empty for AI-generated)</label>
          <textarea
            value={uploadCaption}
            onChange={(e) => setUploadCaption(e.target.value)}
            rows={2}
            placeholder="Write your own caption, or leave empty and toggle 'auto-generate' below"
            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl focus:border-emerald-400 outline-none"
            data-testid="doozyad-upload-caption"
          />
          <label className="mt-2 flex items-center gap-2 text-xs text-[#64748B] cursor-pointer">
            <input
              type="checkbox"
              checked={uploadAutoCaptions}
              onChange={(e) => setUploadAutoCaptions(e.target.checked)}
              className="accent-emerald-500"
              data-testid="doozyad-upload-autocaptions"
            />
            Also generate 4 alternate AI caption variants (you'll pick one at approval time)
          </label>
        </div>

        {uploading && (
          <div className="mt-3">
            <div className="text-xs text-[#64748B] mb-1">Uploading… {uploadProgress}%</div>
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        <button
          onClick={uploadMedia}
          disabled={uploading || !uploadFile}
          className="mt-3 px-4 py-2 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
          data-testid="doozyad-upload-submit-btn"
        >
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload to draft queue</>}
        </button>
      </div>

      {/* Bulk upload panel */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-1">
          <Upload size={16} className="text-amber-600" /> Bulk upload (multiple files at once)
        </h3>
        <p className="text-xs text-[#64748B] mb-4">
          Drop several files at once → one draft is created per file.
          <b> Per-file AI captions</b> mode allows up to 10 files with unique AI captions per file.
          <b> Shared caption</b> mode allows up to 20 files with the same caption applied to all.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Caption mode</label>
            <select
              value={bulkMode}
              onChange={(e) => { setBulkMode(e.target.value); setBulkFiles([]); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-amber-400 outline-none bg-white"
              data-testid="doozyad-bulk-mode"
            >
              <option value="per_file_ai">Per-file AI captions (max 10 files)</option>
              <option value="shared">One shared caption (max 20 files)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Theme tag</label>
            <select
              value={bulkTheme}
              onChange={(e) => setBulkTheme(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-amber-400 outline-none bg-white"
              data-testid="doozyad-bulk-theme"
            >
              {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        {bulkMode === "shared" && (
          <div className="mt-3">
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Shared caption (applied to all {bulkFiles.length || "n"} files)</label>
            <textarea
              value={bulkSharedCaption}
              onChange={(e) => setBulkSharedCaption(e.target.value)}
              rows={2}
              placeholder="Same caption applied to every uploaded file"
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl focus:border-amber-400 outline-none"
              data-testid="doozyad-bulk-shared-caption"
            />
          </div>
        )}

        <div className="mt-3">
          <label
            htmlFor="doozyad-bulk-input"
            className="flex items-center gap-2 px-3 py-3 border-2 border-dashed border-gray-300 hover:border-amber-400 bg-white rounded-xl cursor-pointer text-xs"
            data-testid="doozyad-bulk-dropzone"
          >
            <Upload size={16} className="text-gray-400" />
            <span className="text-[#64748B]">
              {bulkFiles.length === 0
                ? `Click or drop up to ${bulkMaxFiles} files (PNG/JPEG/MP4/MOV)`
                : `${bulkFiles.length} file${bulkFiles.length > 1 ? "s" : ""} selected`}
            </span>
          </label>
          <input
            id="doozyad-bulk-input"
            type="file"
            multiple
            accept="image/png,image/jpeg,video/mp4,video/quicktime"
            onChange={(e) => onBulkFilesPicked(e.target.files)}
            className="hidden"
            data-testid="doozyad-bulk-input"
          />
        </div>

        {bulkFiles.length > 0 && (
          <div className="mt-3 max-h-40 overflow-y-auto space-y-1.5">
            {bulkFiles.map((f, i) => (
              <div key={`${f.name}-${f.size}-${f.lastModified || i}`} className="flex items-center gap-2 text-xs px-2 py-1.5 bg-white rounded-lg border border-gray-100">
                {f.type.startsWith("video") ? <Film size={13} className="text-amber-600 shrink-0" /> : <Camera size={13} className="text-amber-600 shrink-0" />}
                <span className="flex-1 truncate text-[#1E1B4B]">{f.name}</span>
                <span className="text-[10px] text-[#64748B]">{(f.size / (1024 * 1024)).toFixed(2)} MB</span>
                <button
                  onClick={() => setBulkFiles(bulkFiles.filter((_, idx) => idx !== i))}
                  className="p-0.5 hover:bg-amber-50 rounded"
                  data-testid={`doozyad-bulk-remove-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {bulkResult && (
          <div className="mt-3 text-xs bg-white border border-amber-200 rounded-lg px-3 py-2" data-testid="doozyad-bulk-result">
            <b className="text-amber-700">Bulk upload complete:</b> {bulkResult.succeeded}/{bulkResult.total} succeeded
            {bulkResult.failed > 0 && <span className="text-red-600"> · {bulkResult.failed} failed</span>}
          </div>
        )}

        <button
          onClick={uploadBulk}
          disabled={bulkUploading || bulkFiles.length === 0}
          className="mt-3 px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
          data-testid="doozyad-bulk-submit-btn"
        >
          {bulkUploading
            ? <><Loader2 size={14} className="animate-spin" /> Uploading {bulkFiles.length} files…</>
            : <><Upload size={14} /> Upload {bulkFiles.length || ""} file{bulkFiles.length === 1 ? "" : "s"} → drafts</>}
        </button>
      </div>
      </>)}

      {/* Tab nav */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1 flex-wrap">
          {[
            { id: "overview", label: "Overview", icon: LayoutDashboard },
            { id: "brain", label: "Brain", icon: Brain },
            { id: "weekly", label: "Weekly Plan", icon: Calendar },
            { id: "queue", label: `Pending (${drafts.length})`, icon: Eye },
            { id: "scheduled", label: `Scheduled (${scheduled.length})`, icon: Calendar },
            { id: "published", label: `Published (${published.length})`, icon: CheckCircle2 },
            { id: "trends", label: `Trends (${festivals.filter((f) => f.is_due_soon).length})`, icon: TrendingUp },
            { id: "retarget", label: "Retarget", icon: Send },
            { id: "influencers", label: "Influencers", icon: UserPlus },
            { id: "dashboard", label: "7-day Dashboard", icon: BarChart3 },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px ${activeTab === t.id ? "border-[#DC283C] text-[#DC283C]" : "border-transparent text-[#64748B] hover:text-[#1E1B4B]"}`}
              data-testid={`doozyad-tab-${t.id}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview / Command Center — Phase 3 capstone */}
      {activeTab === "overview" && (
        <>
          <BrandPositionToggle authAxios={authAxios} />
          <CommandCenter
            data={commandCenter}
            roas={roas}
            onJumpTab={setActiveTab}
            onRefresh={loadCommandCenter}
          />
        </>
      )}

      {/* Weekly Plan — visible 7-day cron calendar */}
      {activeTab === "weekly" && <WeeklyPlanView authAxios={authAxios} />}

      {/* Brain — autonomous marketing director (RAG + Gemini) */}
      {activeTab === "brain" && <BrainPanel authAxios={authAxios} />}

      {/* Pending queue */}
      {activeTab === "queue" && (
        drafts.length === 0 ? (
          <EmptyState icon={Eye} title="No drafts pending" subtitle="Generate one above to start." />
        ) : (
          <div className="space-y-4">
            {drafts.map((d) => (
              <DraftCard key={d.draft_id} draft={d} authAxios={authAxios} onChange={refreshAll} canPublish={config?.meta?.fully_configured} />
            ))}
          </div>
        )
      )}

      {/* Scheduled */}
      {activeTab === "scheduled" && (
        scheduled.length === 0 ? (
          <EmptyState icon={Calendar} title="No drafts scheduled" subtitle="Schedule a pending draft from the Approve panel." />
        ) : (
          <div className="space-y-4">
            {scheduled.map((d) => (
              <ScheduledCard key={d.draft_id} draft={d} authAxios={authAxios} onChange={refreshAll} />
            ))}
          </div>
        )
      )}

      {/* Trends — Phase 3 Sprint 1 */}
      {activeTab === "trends" && (
        <TrendsPanel
          festivals={festivals}
          googleTrends={googleTrends}
          onRunFestival={runFestivalPass}
          onRunBrainstorm={runTrendBrainstorm}
          festivalRunning={festivalRunning}
          trendBrainstorming={trendBrainstorming}
        />
      )}

      {/* Retargeting — Phase 3 Sprint 2 */}
      {activeTab === "retarget" && (
        <RetargetPanel
          queue={retargetQueue}
          audienceFilter={retargetAudienceFilter}
          setAudienceFilter={setRetargetAudienceFilter}
          onScan={runRetargetScan}
          scanning={retargetScanning}
          authAxios={authAxios}
          onRefresh={loadRetargetQueue}
        />
      )}

      {/* Influencer Outreach — Phase 3 Sprint 3 */}
      {activeTab === "influencers" && (
        <>
          <DiscoverHashtagPanel
            hashtag={discoverHashtag}
            setHashtag={setDiscoverHashtag}
            running={discoverRunning}
            result={discoverResult}
            onRun={runDiscover}
          />
          <InfluencersPanel
            influencers={influencers}
            statusFilter={influencerStatusFilter}
            setStatusFilter={setInfluencerStatusFilter}
            authAxios={authAxios}
            onRefresh={loadInfluencers}
          />
        </>
      )}

      {/* Published */}
      {activeTab === "published" && (
        published.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="No published posts yet" subtitle="Approved drafts will land here." />
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {published.map((p) => (
              <PublishedCard key={p.draft_id + (p.approved_at || "")} post={p} authAxios={authAxios} onChange={refreshAll} />
            ))}
          </div>
        )
      )}

      {/* Dashboard */}
      {activeTab === "dashboard" && dashboard && <DashboardCards d={dashboard} />}

      {/* Digest preview modal */}
      {showDigestModal && digestPreview && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDigestModal(false)}
          data-testid="doozyad-digest-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <div>
                <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Mail size={13} /> Sunday digest preview
                </div>
                <div className="text-sm text-[#1E1B4B] font-bold mt-0.5">
                  Week {digestPreview.week} → {digestPreview.recipient}
                </div>
              </div>
              <button
                onClick={() => setShowDigestModal(false)}
                className="p-1.5 hover:bg-white rounded-lg"
                data-testid="doozyad-digest-modal-close"
              >
                <X size={18} />
              </button>
            </div>
            <iframe
              title="digest-preview"
              srcDoc={digestPreview.html}
              className="flex-1 w-full border-0"
              sandbox=""
              data-testid="doozyad-digest-preview-iframe"
            />
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 text-xs text-[#64748B]">
              This is exactly what hits your inbox every Sunday 9 PM IST. The cron is idempotent — never double-sends.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

